import IPaymentTransaction from '../../models/IPaymentTransaction';
import { toNumber } from './paymentDetail';

// Message posted by the hosted payment iframe for Stripe (acct_) merchants. The iframe only
// confirms a SetupIntent (saves the card); graze-ui completes it and charges the card itself.
export interface IStripeSetupIntentMessage {
  setupIntent: { id: string; client_secret?: string };
  // Dollars, usually as strings ("64.64").
  totalAmount?: number | string;
  tipAmount?: number | string;
}

export const isStripeSetupIntentMessage = (data: any): data is IStripeSetupIntentMessage =>
  !!data && typeof data === 'object' &&
  !!data.setupIntent && typeof data.setupIntent === 'object' && typeof data.setupIntent.id === 'string';

// The payment link graze-ui created: its amount (the order total, in cents) and whether HPay shows a surcharge.
export interface IStripePaymentLink {
  amountCents: number;
  applySurcharge: boolean;
}

// Builds the /graze/payments body from the /completegraze payload (amounts in cents).
// /completegraze returns the iframe's totalAmount as amount and its tipAmount as tipAmount, and HPay then
// charges amount + tip with no surcharge of its own. Without surcharge the iframe posts the link amount;
// with surcharge it posts the total it showed (link amount + tip + surcharge), so the tip comes out of amount.
export const toStripeChargePayload = (
  setupPayload: any,
  guest: { name: string; email?: string },
  merchantAccount: string | undefined,
  link: IStripePaymentLink
) => {
  const charge = JSON.parse(JSON.stringify(setupPayload));
  const total = Math.round(toNumber(setupPayload.amount ? setupPayload.amount : setupPayload.totalPaid));
  const tip = setupPayload.tipAmount ? Math.round(toNumber(setupPayload.tipAmount)) : 0;
  if (!Number.isFinite(total) || total <= 0) {
    throw new Error('The setup intent payload has no amount to charge.');
  }
  if (!Number.isFinite(tip) || tip < 0) {
    throw new Error('The setup intent payload has an invalid tip.');
  }
  charge.tip = tip;
  charge.amount = link.applySurcharge ? total - tip : total;
  if (link.applySurcharge && charge.amount < link.amountCents) {
    throw new Error('The setup intent total is less than the payment link amount.');
  }
  if (setupPayload.nameOnCard === '{NAMEONCARD}') {
    charge.nameOnCard = guest.name;
  }
  if (setupPayload.payerEmail === '{EMAIL}') {
    charge.payerEmail = guest.email;
  }
  const cardHolder = charge.payment_method?.credit_card?.card_holder;
  if (cardHolder?.holder_name === '{NAMEONCARD}') {
    cardHolder.holder_name = guest.name;
  }
  if (cardHolder?.email === '{EMAIL}') {
    cardHolder.email = guest.email;
  }
  charge.auto_capture = true;
  charge.account_id = merchantAccount;
  return charge;
};

export const toStripePaymentTransaction = (
  paymentResponse: any,
  cardDetails: any,
  charge: any,
  link: IStripePaymentLink
): IPaymentTransaction => {
  const transactionId = paymentResponse?.idPayment || paymentResponse?.id;
  const amount = toNumber(paymentResponse?.amount);
  if (!transactionId || !Number.isFinite(amount) || amount <= 0) {
    throw new Error('The payment response has no transaction id or amount.');
  }
  const tip = toNumber(charge.tip);
  // HPay reports no surcharge on this path, so it is whatever was charged above the link amount and the tip.
  const surcharge = link.applySurcharge ? Math.round(amount) - tip - link.amountCents : 0;
  const issuer: string = cardDetails?.credit_card?.issue || '';
  return {
    cCTransactionId: String(transactionId),
    paymentProcessorTransactionId: String(transactionId),
    transactionState: 3,
    transactionType: 'Online',
    paymentMethod: 'Credit Card',
    paymentMethodId: paymentResponse.payment_method?.id,
    cardIssuer: issuer && issuer.charAt(0).toUpperCase() + issuer.slice(1),
    maskedCardNumber: cardDetails?.credit_card?.display_name || '',
    madeBy: '',
    receivedBy: '',
    comment: cardDetails?.status || charge.status,
    cCPostal: cardDetails?.credit_card?.card_holder?.address?.postal_code || charge.zipCode,
    cCStatus: paymentResponse.status != null ? String(paymentResponse.status) : undefined,
    cCAuthCode: paymentResponse.authorization_code,
    totalPaid: amount / 100,
    totalTip: tip > 0 ? tip / 100 : 0,
    totalRefund: 0,
    totalSurcharge: surcharge > 0 ? surcharge / 100 : 0,
    dateApplied: new Date(),
  };
};
